import React, { useCallback, useEffect, useState } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination, Card } from 'react-bootstrap';
import { FileCheck2, Download, Search, RotateCcw } from 'lucide-react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import {
  CREDENCIAL_LABEL, RESGUARDO_LABEL, describirResultado,
  descargarConstanciaEliminacion, listarConstanciasEliminacion,
} from '../utils/constanciasEliminacion';
import './Bitacora.css';

const PAGE_SIZE = 25;

const formatFecha = (c) => {
  if (!c?.ocurridoEnMexico) return '—';
  return new Date(c.ocurridoEnMexico).toLocaleString('es-MX', {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

/** Constancias de eliminación de credenciales de la cuenta (AB#763), con filtro por RFC y descarga en PDF. */
const ConstanciasEliminacion = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const [rfcInput, setRfcInput] = useState('');
  const [query, setQuery] = useState({ rfc: '', page: 1, pageSize: PAGE_SIZE });
  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [descargando, setDescargando] = useState(null);

  const cargar = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await listarConstanciasEliminacion(q, getAuthHeaders));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { cargar(query); }, [query, cargar]);

  const buscar = () => setQuery({ rfc: rfcInput.trim().toUpperCase(), page: 1, pageSize: PAGE_SIZE });
  const limpiar = () => { setRfcInput(''); setQuery({ rfc: '', page: 1, pageSize: PAGE_SIZE }); };
  const irA = (page) => setQuery((q) => ({ ...q, page }));

  const descargar = async (folio) => {
    setDescargando(folio);
    try {
      await descargarConstanciaEliminacion(folio, getAuthHeaders);
    } catch (err) {
      setError(err.message);
    } finally {
      setDescargando(null);
    }
  };

  const renderPaginacion = () => {
    if (result.totalPages <= 1) return null;
    const actual = result.page;
    const total = result.totalPages;
    const desde = Math.max(1, actual - 2);
    const hasta = Math.min(total, desde + 4);
    const paginas = [];
    for (let p = desde; p <= hasta; p += 1) paginas.push(p);
    return (
      <Pagination className="mb-0">
        <Pagination.Prev onClick={() => irA(actual - 1)} disabled={actual === 1} />
        {paginas.map((p) => (
          <Pagination.Item key={p} active={p === actual} onClick={() => irA(p)}>{p}</Pagination.Item>
        ))}
        <Pagination.Next onClick={() => irA(actual + 1)} disabled={actual === total} />
      </Pagination>
    );
  };

  return (
    <div className="bitacora-container">
      <div className="bitacora-header">
        <div>
          <h2 className="bitacora-title">
            <FileCheck2 size={24} className="me-2" />
            Constancias de eliminación
          </h2>
          <p className="bitacora-subtitle">
            Cada vez que eliminas la CIEC o la e.firma de un RFC, SatGo emite una constancia con folio que acredita
            que la custodia terminó. Descárgala en PDF para entregarla a tu contribuyente o a una auditoría.
          </p>
        </div>
      </div>

      <Card className="bitacora-filtros mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label className="bitacora-label" htmlFor="constancias-rfc">RFC</Form.Label>
              <Form.Control
                id="constancias-rfc"
                type="text"
                placeholder="Todos"
                value={rfcInput}
                onChange={(e) => setRfcInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
              />
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button variant="primary" onClick={buscar}>
                <Search size={16} className="me-1" />
                Buscar
              </Button>
              <Button variant="outline-secondary" onClick={limpiar} title="Limpiar filtro">
                <RotateCcw size={16} />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <div className="bitacora-toolbar">
        <span className="bitacora-total">
          {loading ? 'Cargando…' : `${result.totalCount.toLocaleString('es-MX')} ${result.totalCount === 1 ? 'constancia' : 'constancias'}`}
        </span>
      </div>

      <Card className="bitacora-tabla-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="bitacora-loading"><Spinner animation="border" variant="primary" /></div>
          ) : result.items.length === 0 ? (
            <div className="bitacora-vacio">
              {query.rfc
                ? `No hay constancias para el RFC ${query.rfc}.`
                : 'Aún no hay constancias. Se emiten al eliminar la CIEC o la e.firma de un RFC, o el RFC completo.'}
            </div>
          ) : (
            <Table responsive hover className="bitacora-tabla mb-0">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>RFC</th>
                  <th>Credencial</th>
                  <th>Resguardo</th>
                  <th>Resultado</th>
                  <th>Fecha (hora de México)</th>
                  <th aria-label="Descarga" />
                </tr>
              </thead>
              <tbody>
                {result.items.map((c) => (
                  <tr key={c.folio}>
                    <td><code>{c.folio}</code></td>
                    <td>{c.rfc}</td>
                    <td>{CREDENCIAL_LABEL[c.credencial] || c.credencial}</td>
                    <td>{RESGUARDO_LABEL[c.tipoCredencial] || c.tipoCredencial}</td>
                    <td title={c.nota || undefined}>{describirResultado(c)}</td>
                    <td title={`UTC: ${c.ocurridoEnUtc}`}>{formatFecha(c)}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" onClick={() => descargar(c.folio)}
                        disabled={descargando === c.folio} className="d-inline-flex align-items-center gap-1">
                        {descargando === c.folio ? <Spinner animation="border" size="sm" /> : <Download size={14} />}
                        PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <div className="bitacora-paginacion">{renderPaginacion()}</div>
    </div>
  );
};

export default ConstanciasEliminacion;
